// src/AdminDashbord/NewsManager.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Component,
} from "react";
import { db } from "../firebase-config";
import { getAuth } from "firebase/auth";
import { getDoc } from "firebase/firestore";
import CustomEditor from "../Components/CustomTexteditor";
import GoogleDriveImage from "./GoogleDriveImage";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

/* ---------------- Notification ---------------- */
function Notification({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div
      className={`alert alert-${
        type === "error" ? "danger" : "success"
      } alert-dismissible`}
    >
      {message}
      <button className="btn-close" onClick={onClose}></button>
    </div>
  );
}

/* ---------------- Error Boundary ---------------- */
class EditorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      const { value, setValue } = this.props;
      return (
        <textarea
          className="form-control mb-2"
          rows={6}
          value={value || ""}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    }
    return this.props.children;
  }
}

/* ---------------- Helpers ---------------- */
const formatDate = (ts) => {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d.toLocaleString();
};

/* ---------------- MAIN ---------------- */
function NewsManager() {
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [author, setAuthor] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [featured, setFeatured] = useState(false);

  const [imageSource, setImageSource] = useState("cloudinary");
  const [driveImageUrl, setDriveImageUrl] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");

  const [items, setItems] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "success" });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showImageChangeDialog, setShowImageChangeDialog] = useState(false);

  const headingRef = useRef(null);

  /* ---------------- AUTH ---------------- */
  useEffect(() => {
    const auth = getAuth();
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        const snap = await getDoc(doc(db, "adminUsers", user.uid));
        setAuthor(snap.exists() ? snap.data().name : user.displayName || "Admin");
      }
    });
  }, []);

  const showNotificationMsg = (msg, type = "success") => {
    setNotification({ message: msg, type });
    setTimeout(() => setNotification({ message: "", type }), 2500);
  };

  /* ---------------- FETCH ---------------- */
  const fetchItems = useCallback(async () => {
    const snap = await getDocs(collection(db, "news"));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setItems(data);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  /* ---------------- SAVE ---------------- */
  const saveNews = async (finalImageUrl) => {
    const payload = {
      title,
      shortDescription,
      description,
      imageUrl: finalImageUrl || editImageUrl || "",
      tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      author,
      sourceUrl,
      featured,
      updatedAt: serverTimestamp(),
    };

    if (isEditing) {
      await updateDoc(doc(db, "news", editId), payload);
      showNotificationMsg("News updated!");
    } else {
      await addDoc(collection(db, "news"), {
        ...payload,
        timestamp: serverTimestamp(),
      });
      showNotificationMsg("News added!");
    }

    resetForm();
    fetchItems();
  };

  /* ---------------- UPLOAD ---------------- */
  const handleUpload = (keepOld = false) => {
    if (!title || !description) {
      showNotificationMsg("Title & description required", "error");
      return;
    }

    if (imageSource === "url") {
      saveNews(driveImageUrl);
      return;
    }

    if (keepOld) {
      saveNews(editImageUrl);
      return;
    }

    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: "dgxhp09em",
        uploadPreset: "unsigned_preset",
      },
      async (error, result) => {
        if (!error && result?.event === "success") {
          saveNews(result.info.secure_url);
        } else {
          saveNews(editImageUrl);
        }
      }
    );
    widget.open();
  };

  /* ---------------- EDIT ---------------- */
  const handleEdit = (item) => {
    setIsEditing(true);
    setEditId(item.id);
    setTitle(item.title);
    setShortDescription(item.shortDescription || "");
    setDescription(item.description || "");
    setTags(item.tags?.join(", ") || "");
    setAuthor(item.author || "Admin");
    setSourceUrl(item.sourceUrl || "");
    setFeatured(item.featured || false);
    setEditImageUrl(item.imageUrl || "");
    setImageSource(item.imageUrl ? "url" : "cloudinary");

    headingRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditId(null);
    setEditImageUrl("");
    setDriveImageUrl("");
    setImageSource("cloudinary");
    setTitle("");
    setShortDescription("");
    setDescription("");
    setTags("");
    setSourceUrl("");
    setFeatured(false);
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "news", id));
    fetchItems();
    showNotificationMsg("Deleted!");
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="container mt-4">
      <Notification {...notification} onClose={() => setNotification({ message: "", type: "success" })} />

      <h3 ref={headingRef}>News Manager</h3>

      <div className="card p-3 mb-3">
        <input className="form-control mb-2" placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
        <input className="form-control mb-2" placeholder="Short description" value={shortDescription} onChange={e => setShortDescription(e.target.value)} />

        <EditorBoundary value={description} setValue={setDescription}>
          <CustomEditor value={description} setValue={setDescription} />
        </EditorBoundary>

        <input className="form-control mb-2" placeholder="Tags" value={tags} onChange={e => setTags(e.target.value)} />

        <select className="form-select mb-2" value={imageSource} onChange={e => setImageSource(e.target.value)}>
          <option value="cloudinary">Upload via Cloudinary</option>
          <option value="url">Use Google Drive Image</option>
        </select>

        {imageSource === "url" && (
          <GoogleDriveImage onImageReady={(url) => setDriveImageUrl(url)} />
        )}

        <button className="btn btn-success mt-2" onClick={() => handleUpload()}>
          {isEditing ? "Update News" : "Add News"}
        </button>
      </div>

      <div className="row">
        {items.map(item => (
          <div key={item.id} className="col-md-4">
            <div className="card mb-3">
              {item.imageUrl && <img src={item.imageUrl} className="card-img-top" alt="" />}
              <div className="card-body">
                <h5>{item.title}</h5>
                <small>{formatDate(item.timestamp || item.updatedAt)}</small>
                <div dangerouslySetInnerHTML={{ __html: item.description }} />
                <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(item)}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item.id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default NewsManager;
